/** biome-ignore-all lint/correctness/useUniqueElementIds: single-instance form, same as the self-hosted auth components */
import { useState } from 'react';
import { createTenant } from '@/api/superadmin';
import { Button } from '@/components/primitives/button';
import { CopyButton } from '@/components/primitives/copy-button';
import { Input } from '@/components/primitives/input';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (organizationId: string) => void;
}

/**
 * Create a tenant workspace and invite its admin.
 *
 * When the invitation email cannot be sent (no platform mail configured, or the
 * borrowed integration is broken) the API returns the one-time link instead of
 * failing. Showing it here is the difference between "the feature is broken"
 * and "deliver this link yourself" -- so the link is surfaced prominently
 * rather than buried.
 */
export function CreateTenantSheet({ open, onClose, onCreated }: Props) {
  const [organizationName, setOrganizationName] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  if (!open) {
    return null;
  }

  const reset = () => {
    setOrganizationName('');
    setAdminFirstName('');
    setAdminLastName('');
    setAdminEmail('');
    setError(null);
    setFallbackLink(null);
    setCreatedId(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await createTenant({
        organizationName,
        adminFirstName,
        adminLastName: adminLastName || undefined,
        adminEmail,
      });

      setCreatedId(result.organizationId);

      if (result.invitationSent) {
        reset();
        onCreated(result.organizationId);
        onClose();
      } else {
        // Keep the sheet open: the operator has to copy this link before it is lost.
        setFallbackLink(result.invitationLink ?? null);
      }
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true">
      <div className="bg-bg-white flex h-full w-full max-w-md flex-col gap-5 overflow-auto p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-title-h6 text-text-strong">New tenant</h2>
            <p className="text-paragraph-sm text-text-soft">
              Creates the workspace, both environments, and emails the admin a link to set a password.
            </p>
          </div>
          <button type="button" onClick={close} className="text-text-soft hover:text-text-strong" aria-label="Close">
            ✕
          </button>
        </div>

        {fallbackLink ? (
          <div className="border-warning-base/40 bg-warning-base/5 flex flex-col gap-3 rounded-lg border p-4">
            <p className="text-paragraph-sm text-text-strong">
              The tenant was created, but the invitation email could not be sent.
            </p>
            <p className="text-paragraph-xs text-text-soft">
              Send this one-time link to {adminEmail || 'the administrator'} yourself. It expires in 72 hours and is
              shown only once.
            </p>
            <div className="bg-bg-weak flex items-center gap-2 rounded-md p-2">
              <code className="text-paragraph-xs text-text-sub min-w-0 flex-1 break-all">{fallbackLink}</code>
              <CopyButton valueToCopy={fallbackLink} size="xs" ariaLabel="Copy invitation link" />
            </div>
            <Button
              type="button"
              variant="primary"
              mode="filled"
              onClick={() => {
                const id = createdId;
                reset();
                onClose();
                if (id) onCreated(id);
              }}
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label htmlFor="organizationName" className="text-label-sm text-text-strong mb-1 block">
                Tenant name
              </label>
              <Input
                id="organizationName"
                value={organizationName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrganizationName(e.target.value)}
                placeholder="Acme HRMS"
                required
                className="w-full"
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="adminFirstName" className="text-label-sm text-text-strong mb-1 block">
                  Admin first name
                </label>
                <Input
                  id="adminFirstName"
                  value={adminFirstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminFirstName(e.target.value)}
                  required
                  className="w-full"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="adminLastName" className="text-label-sm text-text-strong mb-1 block">
                  Last name
                </label>
                <Input
                  id="adminLastName"
                  value={adminLastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminLastName(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label htmlFor="adminEmail" className="text-label-sm text-text-strong mb-1 block">
                Admin email
              </label>
              <Input
                id="adminEmail"
                type="email"
                value={adminEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminEmail(e.target.value)}
                placeholder="admin@acme.com"
                required
                className="w-full"
              />
            </div>

            {error && <p className="text-paragraph-sm text-error-base">{error}</p>}

            <div className="mt-2 flex gap-3">
              <Button type="submit" variant="primary" mode="filled" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Creating...' : 'Create tenant'}
              </Button>
              <Button type="button" variant="secondary" mode="outline" onClick={close}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
